import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, MessageSquare, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(params.get("c"));
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [internal, setInternal] = useState(false);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const isStaff = ["admin", "super_admin", "agent", "support"].includes(user?.role);

  const loadConvos = async () => { const { data } = await api.get("/conversations"); setConvos(data); if (!active && data[0]) setActive(data[0].id); };
  useEffect(() => { loadConvos(); /* eslint-disable-next-line */ }, []);

  const loadMessages = async (id) => { if (!id) return; setLoading(true); try { const { data } = await api.get(`/conversations/${id}/messages`); setMessages(data); } finally { setLoading(false); } };
  useEffect(() => { loadMessages(active); const t = setInterval(() => loadMessages(active), 5000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const t = text; setText("");
    await api.post(`/conversations/${active}/messages`, { text: t, internal });
    loadMessages(active); loadConvos();
  };

  const activeConvo = convos.find((c) => c.id === active);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6">
      <h1 className="mb-4 font-heading text-2xl font-bold text-navy">Messages</h1>
      <div className="grid h-[560px] grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white overflow-auto no-scrollbar">
          {convos.length === 0 && <p className="p-6 text-center text-sm text-slate-400">No conversations yet.</p>}
          {convos.map((c) => (
            <button key={c.id} onClick={() => { setActive(c.id); setParams({ c: c.id }); }} data-testid={`convo-${c.id}`}
              className={`flex w-full items-start gap-3 border-b border-slate-100 p-4 text-left hover:bg-slate-50 ${active === c.id ? "bg-brand-light" : ""}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy text-white"><MessageSquare size={15} /></span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-navy">{isStaff ? c.customer_name : c.subject}</span>
                <span className="block truncate text-xs text-slate-500">{c.booking_ref ? `${c.booking_ref} · ` : ""}{c.last_message || c.subject}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="flex flex-col rounded-2xl border border-slate-200 bg-white md:col-span-2">
          {active ? (
            <>
              <div className="border-b border-slate-200 p-4">
                <p className="font-heading font-semibold text-navy">{activeConvo?.subject || "Conversation"}</p>
                {activeConvo?.booking_ref && <p className="text-xs text-slate-500">Booking {activeConvo.booking_ref}</p>}
              </div>
              <div className="flex-1 space-y-3 overflow-auto p-4 no-scrollbar">
                {loading && messages.length === 0 ? <Loader2 className="mx-auto animate-spin text-brand" /> : messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.internal ? "bg-amber-100 text-amber-900 border border-amber-200" : mine ? "bg-brand text-white" : "bg-slate-100 text-slate-800"}`}>
                        {!mine && <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide opacity-70">{m.sender_name} · {m.sender_role}</span>}
                        {m.internal && <span className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold"><Lock size={9} /> Internal note</span>}
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="border-t border-slate-200 p-3">
                {isStaff && (
                  <label className="mb-2 flex items-center gap-2 text-xs text-slate-500">
                    <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="internal-note-toggle" /> Internal note (not visible to customer/driver)
                  </label>
                )}
                <div className="flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" data-testid="chat-input" />
                  <Button onClick={send} className="bg-brand hover:bg-brand/90" data-testid="chat-send"><Send size={16} /></Button>
                </div>
              </div>
            </>
          ) : <div className="flex flex-1 items-center justify-center text-sm text-slate-400">Select a conversation</div>}
        </div>
      </div>
    </div>
  );
}
